/**
 * localStorage Helper
 * 
 * Utility functions for safe localStorage operations with error handling
 * and data validation for the Location Activity Dashboard cache
 * 
 * @author BizClick WMS
 * @version 1.0 - Fase 5 Implementation
 */

/**
 * Check if localStorage is available and functional
 */
export const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__bizclick_storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    console.warn('[localStorage] Not available:', error);
    return false;
  }
};

/**
 * Get item from localStorage with error handling
 * 
 * @param key - localStorage key
 * @returns Stored value or null if not found/error
 */
export const getItem = <T>(key: string): T | null => {
  try {
    if (!isLocalStorageAvailable()) {
      return null;
    }

    const item = localStorage.getItem(key);
    if (!item) {
      return null;
    }

    return JSON.parse(item) as T;
  } catch (error) {
    console.warn(`[localStorage] Error reading key "${key}":`, error);
    return null;
  }
};

/**
 * Set item in localStorage with error handling
 * 
 * @param key - localStorage key
 * @param value - Value to store (will be JSON stringified)
 * @returns Success boolean
 */
export const setItem = <T>(key: string, value: T): boolean => {
  try {
    if (!isLocalStorageAvailable()) {
      return false;
    }

    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    // Handle QuotaExceededError specially
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.error('[localStorage] Storage quota exceeded. Consider clearing old data.');
      return false;
    }

    console.warn(`[localStorage] Error writing key "${key}":`, error);
    return false;
  }
};

/**
 * Remove item from localStorage with error handling
 * 
 * @param key - localStorage key
 * @returns Success boolean
 */
export const removeItem = (key: string): boolean => {
  try {
    if (!isLocalStorageAvailable()) {
      return false;
    }

    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`[localStorage] Error removing key "${key}":`, error);
    return false;
  }
};

/**
 * Clear all items from localStorage (use with caution)
 * 
 * @returns Success boolean
 */
export const clear = (): boolean => {
  try {
    if (!isLocalStorageAvailable()) {
      return false;
    }

    localStorage.clear();
    return true;
  } catch (error) {
    console.warn('[localStorage] Error clearing storage:', error);
    return false;
  }
};

/**
 * Get all keys from localStorage
 * 
 * @returns Array of keys or empty array on error
 */
export const getAllKeys = (): string[] => {
  try {
    if (!isLocalStorageAvailable()) {
      return [];
    }

    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        keys.push(key);
      }
    }
    return keys;
  } catch (error) {
    console.warn('[localStorage] Error getting keys:', error);
    return [];
  }
};

/**
 * Get localStorage usage statistics
 * 
 * @returns Usage statistics or null if not available
 */
export const getStorageStats = (): {
  used: number;
  available: number;
  percentage: number;
} | null => {
  try {
    if (!isLocalStorageAvailable()) {
      return null;
    }

    // Calculate current usage
    let totalSize = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        if (value) {
          // Count key + value size (approximate)
          totalSize += key.length + value.length;
        }
      }
    }

    // Most browsers have 5-10MB localStorage limit
    // We'll assume 5MB (5 * 1024 * 1024 bytes) as conservative estimate
    const estimatedLimit = 5 * 1024 * 1024;

    return {
      used: totalSize,
      available: estimatedLimit - totalSize,
      percentage: (totalSize / estimatedLimit) * 100
    };
  } catch (error) {
    console.warn('[localStorage] Error calculating storage stats:', error);
    return null;
  }
};

/**
 * Validate cache data structure for Activity Cache
 * 
 * @param data - Data to validate
 * @returns True if valid cache data
 */
export const validateCacheData = (data: any): boolean => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  // Check required properties
  if (!Array.isArray(data.events)) {
    return false;
  }

  if (typeof data.savedAt !== 'string') {
    return false;
  }

  if (typeof data.version !== 'string') {
    return false;
  }

  // Validate savedAt is valid ISO date
  try {
    const date = new Date(data.savedAt);
    if (isNaN(date.getTime())) {
      return false;
    }
  } catch (error) {
    return false;
  }

  // Validate each event has minimum required properties
  for (const event of data.events) {
    if (!event.binCode || !event.timestamp || event.activityType === undefined) {
      return false;
    }
  }

  return true;
};

/**
 * Check if cached data is from today (for automatic reset at midnight)
 * 
 * @param savedAt - ISO timestamp when data was saved
 * @returns True if data is from today
 */
export const isCacheFromToday = (savedAt: string): boolean => {
  try {
    const savedDate = new Date(savedAt);
    const today = new Date();

    return (
      savedDate.getFullYear() === today.getFullYear() &&
      savedDate.getMonth() === today.getMonth() &&
      savedDate.getDate() === today.getDate()
    );
  } catch (error) {
    console.warn('[localStorage] Error comparing dates:', error);
    return false;
  }
};

/**
 * Format bytes to human readable string
 * 
 * @param bytes - Number of bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Debounced save function
 * Creates a debounced function that delays saving until after the specified delay
 * 
 * @param func - Function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};
